import { Skeleton } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import classNames from "classnames";
import { SmallBodyHighlight } from "helpers/TypographyComponents";
import { getCleanStartorEndDay } from "helpers/utils";
import debounce from "lodash/debounce";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { IDatesRange, TDatesRangeType } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import RoundBorderChip from "../RoundBorderChip";
import { StyledDatePicker } from "../StyledDatePicker";
import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

export interface IDateRangeShortcut {
    rangeKey: string | number;
    label: string;
    dates: { startDate: Date; endDate: Date };
    /**
     * For periods, know if period is active or closed.
     */
    open?: boolean;
}

interface IDateRangeSelectors {
    /**
     * Label for the component.
     */
    label?: string;
    /**
     * Date range accepted (min date and max date) -- different than the dates chosen
     * which must be within this range
     */
    dateRange: IDatesRange | undefined;
    defaultValues: IDatesRange | undefined;
    /**
     * Triggers function in parent component after they've selected a date.
     * @param updatedDates
     */
    handleChanges: (updatedDates: IDatesRange | undefined) => void;
    /**
     * Shortcuts to display on left side of date picker.
     */
    rangeShortcuts?: IDateRangeShortcut[];
    /**
     * If no maxEndDate specified, the maxDate is end of range.
     */
    maxEndDate?: Date;
    /**
     * Is data needed to get dateRange loading?
     */
    loading?: boolean;
    /**
     * Adds color to fields when true.
     */
    highlight?: boolean;
    /**
     * True if label displayed above (flex column and not row)
     */
    asColumn?: boolean;
}

/**
 * Two date pickers to select start and end date.
 */
const DateRangeSelectors = ({
    label,
    dateRange,
    defaultValues,
    handleChanges,
    rangeShortcuts,
    maxEndDate,
    loading,
    highlight = false,
    asColumn = false,
}: IDateRangeSelectors) => {
    const [showPopover, setShowPopover] = useState({ startDate: false, endDate: false });
    const normalizeRange = useCallback((r?: IDatesRange): IDatesRange | undefined => {
        if (!r) return undefined;
        const normalized: IDatesRange = {} as IDatesRange;
        if (r.startDate) normalized.startDate = getCleanStartorEndDay(r.startDate, { time: "start" });
        if (r.endDate) normalized.endDate = getCleanStartorEndDay(r.endDate, { time: "end" });
        return normalized.startDate || normalized.endDate ? normalized : undefined;
    }, []);

    const [chosenDates, setChosenDates] = useState<IDatesRange | undefined>(normalizeRange(defaultValues));

    /**
     * MaxDate allowed for end of range.
     * If maxEndDate passed, return that.
     * Otherwise, return dateRange.endDate or undefined.
     */
    const maxDate = useMemo(() => {
        if (!maxEndDate) {
            return dateRange?.endDate ? getCleanStartorEndDay(dateRange.endDate, { time: "end" }) : undefined;
        } else {
            return maxEndDate;
        }
    }, [maxEndDate, dateRange]);

    const minDateStart = useMemo(
        () => (dateRange?.startDate ? getCleanStartorEndDay(dateRange.startDate, { time: "start" }) : undefined),
        [dateRange?.startDate],
    );

    // Debounced function to transmit changes to parent component.
    // A useRef and useEffect are used to avoid creating a new debounced function on every render
    const commitDatesChangeRef = useRef<any>(null);
    useEffect(() => {
        commitDatesChangeRef.current = debounce(
            (dates: IDatesRange) => {
                // Commit only when both dates exist and are valid and coherent
                if (!dates.startDate || !dates.endDate) return;

                const start = new Date(dates.startDate);
                const end = new Date(dates.endDate);

                if (
                    !start ||
                    isNaN(start.getTime()) ||
                    !end ||
                    isNaN(end.getTime()) ||
                    (dateRange?.startDate && (start < dateRange.startDate || end < dateRange.startDate)) ||
                    (maxDate && (start > maxDate || end > maxDate)) ||
                    start > end
                ) {
                    return;
                }

                handleChanges({
                    startDate: getCleanStartorEndDay(start, { time: "start" }),
                    endDate: getCleanStartorEndDay(end, { time: "end" }),
                });
            },
            1000,
            { leading: true, trailing: true },
        );

        return () => {
            commitDatesChangeRef.current.cancel();
        };
    }, [handleChanges, dateRange?.startDate, dateRange?.endDate, maxDate]);

    /**
     * Saves changes only when they clicked on a date (otherwise onChange gets triggered when they switch years/months)
     * then it saves values for the DatePicker component
     * and triggers fetch in parent component for new data.
     */
    const handleDateSelection = useCallback(
        (date: Date, type: TDatesRangeType) => {
            const normalizedDate = date
                ? getCleanStartorEndDay(date, { time: type === "endDate" ? "end" : "start" })
                : date;
            const newDates = { ...(chosenDates ?? {}), [type]: normalizedDate } as IDatesRange;
            setChosenDates(newDates);
            commitDatesChangeRef.current(newDates);
        },
        [chosenDates],
    );

    /**
     * Sets date on two selectors depending on shortcut chosen.
     * @param dates S
     */
    const triggerShortcut = (dates: IDatesRange | undefined, reset = false) => {
        if (reset) {
            const normalized = normalizeRange(dateRange);
            setChosenDates(normalized);
            if (normalized) commitDatesChangeRef.current(normalized);
        } else if (dates) {
            const normalized = normalizeRange(dates);
            setChosenDates(normalized);
            if (normalized) commitDatesChangeRef.current(normalized);
        }
        setShowPopover({ startDate: false, endDate: false });
    };

    useEffect(() => {
        if (defaultValues) {
            const normalized = normalizeRange(defaultValues);
            if (normalized) setChosenDates(normalized);
        }
    }, [defaultValues, normalizeRange]);

    const inputSx = { height: "1.1em", backgroundColor: highlight ? `${variables["info-light"]} !important` : "" };

    const renderDateInput = (params: TextFieldProps, onOpen: () => void) => (
        <div className={styles["date-range-input-slot"]}>
            <StyledTextField
                {...params}
                onClick={() => {
                    if (loading) return;
                    onOpen();
                }}
                disabled={loading}
                inputProps={{
                    ...params.inputProps,
                    sx: { ...inputSx },
                    className: styles["date-range-inputs"],
                }}
                InputProps={{
                    ...params.InputProps,
                    sx: highlight
                        ? { "& fieldset": { borderColor: `${variables["info"]} !important` } }
                        : params.InputProps?.sx,
                }}
                sx={{
                    ...(loading ? { visibility: "hidden" } : {}),
                    width: "100%",
                }}
            />
            {loading && <Skeleton variant="rounded" animation="wave" className={styles["date-range-input-skeleton"]} />}
        </div>
    );

    return (
        <div className={classNames({ [styles["date-range-container"]]: true, [styles["column"]]: asColumn })}>
            {label && <span>{label}</span>}
            <div className={styles["date-selectors"]}>
                <StyledDatePicker
                    value={chosenDates?.startDate}
                    open={showPopover.startDate}
                    onChange={(date) => handleDateSelection(date, "startDate")}
                    onAccept={() => setShowPopover({ startDate: false, endDate: false })}
                    onClose={() => setShowPopover({ startDate: false, endDate: false })}
                    disableOpenPicker
                    minDate={minDateStart}
                    // MaxDate for first picker cannot be higher than endDate
                    maxDate={chosenDates?.endDate}
                    renderInput={(params) =>
                        renderDateInput(params, () => setShowPopover({ startDate: true, endDate: false }))
                    }
                    components={{
                        PaperContent: (props) => (
                            <DatePickerPanel
                                {...props}
                                triggerShortcut={triggerShortcut}
                                rangeShortcuts={rangeShortcuts}
                                startDate={dateRange?.startDate}
                            />
                        ),
                    }}
                    PaperProps={{ sx: { display: "flex", flexDirection: "row" } }}
                />
                <span>-</span>
                <StyledDatePicker
                    value={chosenDates?.endDate}
                    onChange={(date) => handleDateSelection(date, "endDate")}
                    onAccept={() => setShowPopover({ startDate: false, endDate: false })}
                    open={showPopover.endDate}
                    onClose={() => setShowPopover({ startDate: false, endDate: false })}
                    disableOpenPicker
                    minDate={chosenDates?.startDate ?? minDateStart}
                    // Either maxDate passed or last date of range.
                    maxDate={maxDate}
                    renderInput={(params) =>
                        renderDateInput(params, () => setShowPopover({ startDate: false, endDate: true }))
                    }
                    components={{
                        PaperContent: (props) => (
                            <DatePickerPanel
                                {...props}
                                triggerShortcut={triggerShortcut}
                                rangeShortcuts={rangeShortcuts}
                                startDate={dateRange?.startDate}
                            />
                        ),
                    }}
                    PaperProps={{ sx: { display: "flex", flexDirection: "row" } }}
                />
            </div>
        </div>
    );
};

/**
 * Panel containing date picker and shortcuts.
 * Shortcuts appear only if rangeShorcuts array containes at least one item.
 */
const DatePickerPanel = ({
    rangeShortcuts,
    triggerShortcut,
    startDate,
    children,
}: {
    rangeShortcuts: IDateRangeShortcut[] | undefined;
    triggerShortcut: (dates: IDatesRange | undefined, reset?: boolean) => void;
    startDate: Date | undefined;
    children: ReactNode;
}) => {
    const [t] = useTranslation();
    return (
        <React.Fragment>
            {rangeShortcuts && rangeShortcuts.length > 0 ? (
                <div className={styles["shortcuts-selector"]}>
                    <SmallBodyHighlight>{t("DateRangeSelectors.QuickSelect")}</SmallBodyHighlight>
                    <div className={styles["chips"]}>
                        {rangeShortcuts &&
                            rangeShortcuts.map((shortcut) => (
                                <RoundBorderChip
                                    text={shortcut.label}
                                    key={shortcut.rangeKey}
                                    color={
                                        shortcut.open !== undefined ? (shortcut.open ? "#848b4f" : "#b55d5d") : "grey"
                                    }
                                    styleOptions={{
                                        outlined: true,
                                        hideDot: shortcut.open === undefined,
                                        onlyColorDot: true,
                                    }}
                                    onClick={() => triggerShortcut(shortcut.dates)}
                                    style={{ cursor: "pointer" }}
                                />
                            ))}
                        <RoundBorderChip
                            text={t("DateRangeSelectors.ToDate")}
                            key={"toDate"}
                            styleOptions={{ hideDot: true, outlined: true }}
                            color={"grey"}
                            onClick={() => triggerShortcut({ startDate: startDate ?? new Date(), endDate: new Date() })}
                            style={{ cursor: "pointer" }}
                        />
                        <RoundBorderChip
                            text={t("DateRangeSelectors.AllDates")}
                            key={"allDates"}
                            styleOptions={{ hideDot: true, outlined: true }}
                            color={"grey"}
                            onClick={() => triggerShortcut(undefined, true)}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
            ) : (
                <></>
            )}

            {children}
        </React.Fragment>
    );
};

export default DateRangeSelectors;
